import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Select, InputNumber, Input, Button, Alert, Space, Modal, Checkbox, message } from 'antd';
import { ThunderboltOutlined, ShoppingOutlined, InfoCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { productsApi, metaAdsApi } from '../../utils/api';
import ChampRessource from './ChampRessource';

/**
 * Formulaire de brief publicitaire : l'utilisateur choisit un objectif, des produits,
 * un budget journalier et une durée, puis demande la génération du brouillon par l'IA.
 *
 * Ce composant ne crée rien sur Meta : il transmet le brief au parent via onGenerate.
 */

const { TextArea } = Input;

// Pays proposés par défaut pour le ciblage (saisie libre possible via le mode "tags")
const PAYS_SUGGERES = [
  { code: 'CI', nom: "Côte d'Ivoire" },
  { code: 'SN', nom: 'Sénégal' },
  { code: 'ML', nom: 'Mali' },
  { code: 'BF', nom: 'Burkina Faso' },
  { code: 'GN', nom: 'Guinée' },
  { code: 'BJ', nom: 'Bénin' },
  { code: 'TG', nom: 'Togo' },
  { code: 'CM', nom: 'Cameroun' },
  { code: 'FR', nom: 'France' }
];

/**
 * Propose le minimum cohérent avec la devise affichée. Le serveur revalide toujours
 * cette limite avant génération et publication pour empêcher une dépense incorrecte.
 */
const getMinimumBudget = (currency) => (currency === 'USD' ? 1 : 1000);

const MetaAdsBriefForm = ({ onGenerate, generating, initialValues = null }) => {
  const [form] = Form.useForm();
  const [formulaireProspect] = Form.useForm();
  const [objectives, setObjectives] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [objectifSelectionne, setObjectifSelectionne] = useState(null);
  // Ressources Meta (Pages, pixels, catalogues) pour proposer des listes plutôt qu'une saisie d'ID
  const [assets, setAssets] = useState(null);
  // Chargement / erreur propres à la liste des objectifs (affichés dans le Select)
  const [loadingObjectives, setLoadingObjectives] = useState(true);
  const [erreurObjectifs, setErreurObjectifs] = useState(null);
  // État local des formulaires instantanés de la Page sélectionnée pour le brief.
  const [formulairesProspects, setFormulairesProspects] = useState([]);
  const [chargementFormulaires, setChargementFormulaires] = useState(false);
  const [creationFormulaireOuverte, setCreationFormulaireOuverte] = useState(false);
  const [creationFormulaire, setCreationFormulaire] = useState(false);
  const [pageFormulaire, setPageFormulaire] = useState(null);

  // Charger les objectifs : appel rapide (liste locale au serveur), indépendant des ressources Meta
  useEffect(() => {
    const chargerObjectifs = async () => {
      try {
        setLoadingObjectives(true);
        setErreurObjectifs(null);
        const { data } = await metaAdsApi.getObjectives();
        setObjectives(data.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des objectifs Meta Ads:', error);
        setErreurObjectifs("Impossible de charger les objectifs de campagne.");
      } finally {
        setLoadingObjectives(false);
      }
    };
    chargerObjectifs();
  }, []);

  // Préremplit les ressources déjà enregistrées pour que le formulaire prospect
  // puisse charger immédiatement les formulaires liés à la Page par défaut.
  useEffect(() => {
    const chargerConfigurationCampagne = async () => {
      try {
        const { data } = await metaAdsApi.getSettings();
        const configuration = data?.data || {};
        const valeurs = {};
        if (configuration.meta_ads_whatsapp_number) valeurs.whatsappNumber = configuration.meta_ads_whatsapp_number;
        if (configuration.meta_ads_page_id) {
          valeurs.pageId = configuration.meta_ads_page_id;
          setPageFormulaire(configuration.meta_ads_page_id);
        }
        if (Object.keys(valeurs).length > 0) form.setFieldValue('overrides', valeurs);
      } catch (error) {
        console.warn('Configuration Meta Ads indisponible:', error.message);
      }
    };
    chargerConfigurationCampagne();
  }, [form]);

  // Charger les ressources Meta séparément : cet appel interroge l'API Graph et peut être lent,
  // il ne doit donc pas retarder l'affichage des objectifs.
  useEffect(() => {
    const chargerRessources = async () => {
      try {
        const { data } = await metaAdsApi.getAssets();
        setAssets(data.data || null);
      } catch (error) {
        console.error('Erreur lors du chargement des ressources Meta:', error);
      }
    };
    chargerRessources();
  }, []);

  // Rechercher des produits (recherche serveur, limitée aux premiers résultats)
  const rechercherProduits = useCallback(async (searchTerm = '') => {
    try {
      setLoadingProducts(true);
      const { data } = await productsApi.getProductsForCatalog({
        page: 1,
        pageSize: 30,
        searchTerm
      });
      setProducts(data.products || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    rechercherProduits();
  }, [rechercherProduits]);

  // Lorsqu'un brouillon de brief est repris, ses valeurs sont réinjectées dans le
  // formulaire afin que l'utilisateur puisse relancer l'analyse sans tout ressaisir.
  useEffect(() => {
    if (!initialValues) return;
    form.setFieldsValue(initialValues);
    setObjectifSelectionne(initialValues.objective || null);
    // Une reprise de brouillon doit cibler la même Page que lors de la première
    // saisie afin de recharger automatiquement le formulaire prospect choisi.
    if (initialValues.overrides?.pageId) setPageFormulaire(initialValues.overrides.pageId);
  }, [form, initialValues]);

  /**
   * Charge les formulaires instantanés de la Page sélectionnée afin de ne proposer
   * que des formulaires réellement utilisables dans une publicité de prospection.
   * @param {string} pageId - Page Facebook qui possède les formulaires à afficher.
   * @returns {Promise<void>} Résolution après mise à jour de la liste locale.
   */
  const chargerFormulairesProspects = useCallback(async (pageId) => {
    if (!pageId) {
      setFormulairesProspects([]);
      return;
    }
    try {
      setChargementFormulaires(true);
      const { data } = await metaAdsApi.getLeadForms(pageId);
      setFormulairesProspects(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des formulaires prospects:', error);
      messageErreurFormulaires(error);
      setFormulairesProspects([]);
    } finally {
      setChargementFormulaires(false);
    }
  }, []);

  /**
   * Affiche une erreur claire sans bloquer le reste de la création de campagne.
   * @param {Error} error - Erreur retournée par l'API Meta ou le serveur Wabot.
   * @returns {void} Aucun retour, un message Ant Design est affiché.
   */
  const messageErreurFormulaires = (error) => {
    // Le champ message est contrôlé par le serveur et explique par exemple une permission Meta manquante.
    const texte = error.response?.data?.message || 'Impossible de charger les formulaires prospects de cette Page.';
    console.warn(texte);
    message.error(texte);
  };

  // Lorsqu'on arrive sur l'objectif Prospect, la liste est actualisée ; elle est
  // également rechargée si l'utilisateur change la Page annoncée dans le brief.
  useEffect(() => {
    if (objectifSelectionne === 'prospecting') chargerFormulairesProspects(pageFormulaire);
  }, [objectifSelectionne, pageFormulaire, chargerFormulairesProspects]);

  /**
   * Crée un formulaire instantané Meta, le sélectionne aussitôt et le rend
   * disponible au brouillon sans demander à l'utilisateur de relancer la page.
   * @param {Object} valeurs - Valeurs validées par la fenêtre de création.
   * @returns {Promise<void>} Résolution après sélection du formulaire créé.
   */
  const creerFormulaireProspect = async (valeurs) => {
    try {
      setCreationFormulaire(true);
      const { data } = await metaAdsApi.createLeadForm({ ...valeurs, pageId: pageFormulaire });
      const formulaireCree = data.data;
      setFormulairesProspects(precedents => [formulaireCree, ...precedents]);
      form.setFieldValue(['overrides', 'leadFormId'], formulaireCree.id);
      formulaireProspect.resetFields();
      setCreationFormulaireOuverte(false);
    } catch (error) {
      // Les règles Meta (notamment politique de confidentialité) restent la source de vérité.
      const texte = error.response?.data?.message || 'Impossible de créer le formulaire prospect.';
      formulaireProspect.setFields([{ name: 'privacyPolicyUrl', errors: [texte] }]);
    } finally {
      setCreationFormulaire(false);
    }
  };

  /**
   * Synchronise la Page locale à chaque saisie afin que la sélection de formulaire
   * cible toujours la même Page que celle déclarée pour la publicité.
   * @param {Object} valeursModifiees - Partie du brief modifiée par l'utilisateur.
   * @returns {void} Met à jour seulement la Page utile aux formulaires prospects.
   */
  const gererValeursModifiees = (valeursModifiees) => {
    if (valeursModifiees.overrides?.pageId !== undefined) {
      setPageFormulaire(valeursModifiees.overrides.pageId || null);
      form.setFieldValue(['overrides', 'leadFormId'], undefined);
    }
  };

  // Objectif actuellement sélectionné (pour afficher ses prérequis)
  const objectifActif = objectives.find(o => o.key === objectifSelectionne);

  // Champs de configuration complétables directement dans ce formulaire.
  // La clé correspond à la colonne renvoyée dans missingSettings ; `options` alimente
  // la liste déroulante quand la ressource a pu être récupérée depuis Meta.
  const CHAMPS_COMPLEMENT = {
    meta_ads_page_id: {
      nom: ['overrides', 'pageId'],
      label: 'Page Facebook',
      placeholder: 'ID de votre Page Facebook',
      aide: 'business.facebook.com → Comptes → Pages',
      options: (assets?.pages || []).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))
    },
    meta_ads_whatsapp_number: {
      nom: ['overrides', 'whatsappNumber'],
      label: 'Numéro WhatsApp Business',
      placeholder: '+221771234567',
      aide: 'Numéro rattaché à votre Page, au format international',
      // Aucune API ne liste les numéros : saisie libre
      options: []
    },
    meta_ads_pixel_id: {
      nom: ['overrides', 'pixelId'],
      label: 'Pixel',
      placeholder: 'Ex: 1234567890123456',
      aide: "Pixel du compte publicitaire, utilisé pour optimiser sur les achats",
      options: (assets?.pixels || []).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))
    },
    meta_ads_catalog_id: {
      nom: ['overrides', 'catalogId'],
      label: 'Catalogue produits',
      placeholder: 'ID du catalogue Meta',
      aide: 'business.facebook.com → Sources de données → Catalogues',
      options: (assets?.catalogs || []).map(c => ({
        value: c.id,
        label: `${c.name} (${c.product_count ?? 0} produits)`
      }))
    }
  };

  // Champs à compléter pour l'objectif choisi (vide si la configuration est complète)
  const champsACompleter = objectifActif && !objectifActif.available
    ? objectifActif.missingSettings.filter(champ => CHAMPS_COMPLEMENT[champ] && !(objectifSelectionne === 'whatsapp' && champ === 'meta_ads_whatsapp_number'))
    : [];

  // Une destination web est nécessaire pour tous les objectifs sauf WhatsApp
  const besoinUrlSite = objectifActif && objectifActif.destinationType === 'WEBSITE';
  // Devise reçue directement depuis le compte Meta : elle évite qu'un montant XOF soit envoyé en USD.
  const deviseCompte = assets?.account?.currency || 'devise Meta à confirmer';
  const minimumBudget = getMinimumBudget(assets?.account?.currency);

  return (
    <Card
      title={
        <div className="flex items-center space-x-2">
          <ShoppingOutlined className="text-blue-600" />
          <span>Nouvelle campagne</span>
        </div>
      }
      className="shadow-sm"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onGenerate}
        initialValues={{ durationDays: 7, countries: ['CI'] }}
        className="max-w-2xl"
        onValuesChange={gererValeursModifiees}
      >
        <Form.Item
          name="objective"
          label="Objectif de la campagne"
          rules={[{ required: true, message: 'Choisissez un objectif' }]}
        >
          <Select
            placeholder="Que doit faire la publicité ?"
            onChange={setObjectifSelectionne}
            /* Indicateur pendant le chargement, plutôt qu'une liste vide sans explication */
            loading={loadingObjectives}
            notFoundContent={loadingObjectives ? 'Chargement des objectifs…' : (erreurObjectifs || 'Aucun objectif disponible')}
            options={objectives.map(o => ({
              value: o.key,
              // Tous les objectifs sont sélectionnables : ce qui manque se saisit ci-dessous
              label: o.available ? o.label : `${o.label} — à compléter`
            }))}
          />
        </Form.Item>

        {objectifActif && (
          <Alert
            type="info"
            showIcon
            className="mb-4"
            message={objectifActif.description}
          />
        )}

        {/* Configuration manquante, saisissable directement pour cette campagne */}
        {champsACompleter.length > 0 && (
          <Card size="small" className="mb-4 bg-orange-50 border-orange-200">
            <div className="flex items-start space-x-2 mb-3">
              <InfoCircleOutlined className="text-orange-600 mt-1" />
              <div className="text-sm text-orange-800">
                Cet objectif a besoin d'informations absentes de vos paramètres. Renseignez-les ici :
                elles seront enregistrées avec cette campagne, sans modifier vos paramètres.
              </div>
            </div>

            {champsACompleter.map(champ => {
              const config = CHAMPS_COMPLEMENT[champ];
              return (
                <Form.Item
                  key={champ}
                  name={config.nom}
                  label={config.label}
                  rules={[{ required: true, message: `${config.label} est requis pour cet objectif` }]}
                  extra={config.aide}
                >
                  {/* Liste déroulante si la ressource a été récupérée, saisie libre sinon */}
                  <ChampRessource options={config.options} placeholder={config.placeholder} />
                </Form.Item>
              );
            })}
          </Card>
        )}

        {/* Destination des annonces : propre à cette campagne, enregistrée avec elle */}
        {besoinUrlSite && (
          <Form.Item
            name={['overrides', 'shopUrl']}
            label="URL de destination"
            extra="Base des liens produits pour cette campagne. Si vide, l'URL du catalogue Facebook est utilisée."
          >
            <Input placeholder="https://maboutique.com/product-detail" />
          </Form.Item>
        )}

        {objectifSelectionne === 'whatsapp' && (
          <Form.Item
            name={['overrides', 'whatsappNumber']}
            label="Numéro WhatsApp utilisé par cette campagne"
            rules={[{ required: true, message: 'Choisissez ou saisissez le numéro WhatsApp de la campagne' }]}
            extra="Ce numéro est envoyé explicitement à Meta. Vous pouvez remplacer le numéro enregistré pour cette campagne uniquement."
          >
            <Input placeholder="+2250700000000" />
          </Form.Item>
        )}

        {objectifSelectionne === 'prospecting' && (
          <Card size="small" className="mb-4 border-blue-200 bg-blue-50">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <div className="font-medium text-blue-950">Formulaire prospect</div>
                <div className="text-sm text-blue-800">Les coordonnées seront demandées directement dans Facebook ou Instagram.</div>
              </div>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => chargerFormulairesProspects(pageFormulaire)} loading={chargementFormulaires} disabled={!pageFormulaire}>
                  Actualiser
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreationFormulaireOuverte(true)} disabled={!pageFormulaire}>
                  Créer un formulaire
                </Button>
              </Space>
            </div>
            {!pageFormulaire && <Alert type="warning" showIcon message="Sélectionnez d’abord la Page Facebook de la campagne." className="mb-3" />}
            <Form.Item
              name={['overrides', 'leadFormId']}
              label="Formulaire à utiliser"
              rules={[{ required: true, message: 'Créez ou choisissez un formulaire prospect' }]}
              extra="Un formulaire créé ici est enregistré dans Meta et reste réutilisable pour vos futures campagnes."
            >
              <Select
                placeholder="Choisir un formulaire Meta"
                loading={chargementFormulaires}
                disabled={!pageFormulaire}
                options={formulairesProspects.map(formulaire => ({
                  value: formulaire.id,
                  label: `${formulaire.name}${formulaire.status ? ` — ${formulaire.status}` : ''}`
                }))}
              />
            </Form.Item>
          </Card>
        )}

        {/* Le catalogue Advantage+ sélectionne lui-même les produits */}
        {objectifActif?.needsProducts !== false && (
          <Form.Item
            name="productIds"
            label="Produits à promouvoir"
            rules={[{ required: true, message: 'Sélectionnez au moins un produit' }]}
            extra="Un ensemble de publicités sera créé par produit"
          >
            <Select
              mode="multiple"
              showSearch
              filterOption={false}
              loading={loadingProducts}
              onSearch={rechercherProduits}
              placeholder="Rechercher un produit"
              options={products.map(p => ({
                value: p.id,
                label: `${p.name}${p.price ? ` — ${p.price} XOF` : ''}`
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="dailyBudget"
          label="Budget journalier"
          rules={[{ required: true, message: 'Indiquez un budget journalier' }]}
          extra={`Minimum ${minimumBudget} ${deviseCompte} par jour, réparti automatiquement entre les produits`}
        >
          <InputNumber
            min={minimumBudget}
            step={assets?.account?.currency === 'USD' ? 1 : 500}
            style={{ width: '100%' }}
            addonAfter={`${deviseCompte} / jour`}
            placeholder="Ex: 10000"
          />
        </Form.Item>

        {assets?.account?.currency && (
          <Alert
            type="info"
            showIcon
            className="mb-4"
            message={`Le budget est envoyé à Meta en ${assets.account.currency}. Vérifiez le montant avant de générer.`}
          />
        )}

        <Form.Item
          name="durationDays"
          label="Durée prévue (jours)"
          extra="Sert à estimer le budget total et à cadrer les recommandations de l'IA"
        >
          <InputNumber min={1} max={90} style={{ width: '100%' }} addonAfter="jours" />
        </Form.Item>

        <Form.Item name="countries" label="Pays ciblés">
          <Select
            mode="tags"
            placeholder="Codes ISO à 2 lettres"
            options={PAYS_SUGGERES.map(p => ({ value: p.code, label: `${p.nom} (${p.code})` }))}
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Consignes pour l'IA (optionnel)"
          extra="Ex : mettre en avant la livraison gratuite, cibler plutôt les femmes, insister sur la promotion"
        >
          <TextArea rows={3} placeholder="Vos consignes particulières" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={generating}
              icon={<ThunderboltOutlined />}
            >
              Générer avec l'IA
            </Button>
            <span className="text-gray-500 text-sm">
              Rien n'est envoyé à Meta à cette étape.
            </span>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        title="Créer un formulaire prospect Meta"
        open={creationFormulaireOuverte}
        onCancel={() => setCreationFormulaireOuverte(false)}
        onOk={() => formulaireProspect.submit()}
        okText="Créer et utiliser"
        confirmLoading={creationFormulaire}
        destroyOnClose
      >
        <Form form={formulaireProspect} layout="vertical" onFinish={creerFormulaireProspect} initialValues={{ questions: ['FULL_NAME', 'PHONE'] }}>
          <Form.Item name="name" label="Nom interne" rules={[{ required: true, message: 'Donnez un nom à ce formulaire' }]}>
            <Input placeholder="Prospects offre rentrée septembre" />
          </Form.Item>
          <Form.Item name="questions" label="Informations demandées" rules={[{ required: true, message: 'Choisissez au moins une information' }]}>
            <Checkbox.Group className="flex flex-col gap-2" options={[
              { label: 'Nom complet', value: 'FULL_NAME' },
              { label: 'Adresse e-mail', value: 'EMAIL' },
              { label: 'Numéro de téléphone', value: 'PHONE' }
            ]} />
          </Form.Item>
          <Form.Item name="privacyPolicyUrl" label="URL de la politique de confidentialité" rules={[{ required: true, type: 'url', message: 'Indiquez une URL valide en https://' }]} extra="Obligatoire : Meta l’affiche avant l’envoi du formulaire.">
            <Input placeholder="https://maboutique.com/confidentialite" />
          </Form.Item>
          <Form.Item name="followUpActionUrl" label="URL après envoi (optionnelle)" extra="Page de remerciement ou offre complémentaire.">
            <Input placeholder="https://maboutique.com/merci" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default MetaAdsBriefForm;
